import React from "react";
import { StyleSheet, View } from "react-native";
import { CF } from "../theme/turquoiseTheme.js";

/** Palette — fluid aquamarine reference, aligned with CF tokens */
const FLUID = {
  top: "#C5F5F0",
  mid: "#7EE2E2",
  base: "#5ED9D5",
  deep: "#4FBDBA",
  waveA: "#A8EFE8",
  waveB: "#6EDCD6",
  waveC: "#99F6E4",
  bubble: "rgba(255, 255, 255, 0.72)",
};

function FluidBlob({ style, color, opacity = 1 }) {
  return (
    <View
      pointerEvents="none"
      style={[blobStyles.blob, style, { backgroundColor: color, opacity }]}
    />
  );
}

function BubbleField() {
  const dots = [
    { top: "12%", right: "8%", size: 6 },
    { top: "18%", right: "14%", size: 4 },
    { top: "24%", right: "6%", size: 5 },
    { top: "32%", right: "18%", size: 3 },
    { top: "38%", right: "10%", size: 7 },
    { top: "46%", right: "22%", size: 4 },
    { top: "52%", right: "12%", size: 5 },
    { top: "58%", right: "28%", size: 3 },
    { top: "20%", right: "26%", size: 4 },
    { top: "44%", right: "5%", size: 6 },
  ];
  return (
    <>
      {dots.map((d, i) => (
        <View
          key={`bubble-${i}`}
          pointerEvents="none"
          style={{
            position: "absolute",
            top: d.top,
            right: d.right,
            width: d.size,
            height: d.size,
            borderRadius: d.size / 2,
            backgroundColor: FLUID.bubble,
          }}
        />
      ))}
    </>
  );
}

/** Soft fluid aquamarine — wavy layers, bubbles, diffused light (reference nav mockup) */
function FluidTealBackdrop() {
  return (
    <View style={fluidStyles.root} pointerEvents="none">
      <View style={fluidStyles.sky} />
      <View style={fluidStyles.midBand} />
      <View style={fluidStyles.floor} />

      <FluidBlob
        color={FLUID.waveA}
        opacity={0.75}
        style={{
          width: 420,
          height: 300,
          top: -90,
          left: -120,
          transform: [{ rotate: "-16deg" }],
        }}
      />
      <FluidBlob
        color={FLUID.waveC}
        opacity={0.55}
        style={{
          width: 360,
          height: 240,
          top: 80,
          left: -60,
          transform: [{ rotate: "8deg" }],
        }}
      />
      <FluidBlob
        color={FLUID.waveB}
        opacity={0.45}
        style={{
          width: 320,
          height: 260,
          top: 200,
          right: -100,
          transform: [{ rotate: "22deg" }],
        }}
      />
      <FluidBlob
        color={FLUID.waveA}
        opacity={0.35}
        style={{
          width: 280,
          height: 200,
          bottom: 140,
          left: 20,
          transform: [{ rotate: "-10deg" }],
        }}
      />
      <FluidBlob
        color="#B8F2EE"
        opacity={0.6}
        style={{
          width: 500,
          height: 180,
          bottom: -40,
          left: -80,
          transform: [{ rotate: "-4deg" }],
        }}
      />

      <View style={fluidStyles.lightWash} />
      <BubbleField />
    </View>
  );
}

/**
 * @param {{ variant?: 'quote' | 'home' | 'default' | 'fluid' }} props
 */
export function InspiredBackground({ variant = "fluid" }) {
  void variant;
  return <FluidTealBackdrop />;
}

/** Glass card surface for content on fluid background */
export const GLASS = {
  card: "rgba(255, 255, 255, 0.62)",
  cardStrong: "rgba(255, 255, 255, 0.78)",
  border: "rgba(255, 255, 255, 0.85)",
  tabBar: "rgba(255, 255, 255, 0.48)",
  tabBarBorder: "rgba(255, 255, 255, 0.65)",
  iconWell: "rgba(183, 240, 237, 0.88)",
  iconWellActive: "rgba(126, 226, 226, 0.95)",
  textDark: "#134E4A",
  textMid: "#0F766E",
  textSoft: "#475569",
  label: "#0D9488",
};

const blobStyles = StyleSheet.create({
  blob: { position: "absolute", borderRadius: 9999 },
});

const fluidStyles = StyleSheet.create({
  root: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: FLUID.mid,
    overflow: "hidden",
  },
  sky: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "38%",
    backgroundColor: FLUID.top,
  },
  midBand: {
    position: "absolute",
    top: "30%",
    left: 0,
    right: 0,
    height: "40%",
    backgroundColor: FLUID.mid,
  },
  floor: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "35%",
    backgroundColor: FLUID.base,
  },
  lightWash: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "55%",
    height: "45%",
    backgroundColor: "rgba(255, 255, 255, 0.18)",
    borderBottomRightRadius: 280,
  },
});

/** Gradient fills for progress category cards */
export const CARD_GRADIENTS = [
  { top: "#0D9488", bottom: "#115E59", accent: "rgba(45, 212, 191, 0.35)" },
  { top: "#14B8A6", bottom: "#0F766E", accent: "rgba(34, 211, 238, 0.28)" },
  { top: "#2DD4BF", bottom: "#0D9488", accent: "rgba(94, 234, 212, 0.3)" },
];
